'use client'

import { useEffect, useRef } from 'react'

// ============================================================================
// useIntersectionObserver - Detect when element enters viewport
// ============================================================================

interface UseIntersectionObserverOptions {
  /** Whether the observer should be active */
  enabled?: boolean
  /** Root margin for intersection detection */
  rootMargin?: string
  /** Intersection threshold */
  threshold?: number
}

/**
 * Headless hook for intersection observation
 * Returns a ref to attach to the sentinel element
 */
export function useIntersectionObserver(
  callback: () => void,
  options: UseIntersectionObserverOptions = {},
) {
  const { enabled = true, rootMargin = '200px', threshold = 0 } = options
  const sentinelRef = useRef<HTMLDivElement>(null)
  const callbackRef = useRef(callback)

  // Keep callback ref updated
  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    if (!enabled || !sentinelRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          callbackRef.current()
        }
      },
      { rootMargin, threshold },
    )

    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [enabled, rootMargin, threshold])

  return sentinelRef
}

// ============================================================================
// getGridTemplate - Calculate CSS grid template for table columns
// ============================================================================

interface ColumnSize {
  getSize: () => number
  // For Column objects (from cells)
  columnDef?: {
    minSize?: number
  }
  // For Header objects (which have column.columnDef)
  column?: {
    columnDef?: {
      minSize?: number
    }
  }
}

/**
 * Pure utility to calculate CSS grid-template-columns value.
 * Last column uses 1fr to extend to the edge of the table.
 * If the last column has a minSize, uses minmax(minSize, 1fr) to prevent shrinking.
 *
 * @param columns - Array of objects with getSize() method (TanStack Table headers/cells)
 * @returns CSS grid-template-columns string (e.g., "80px 100px 140px minmax(250px, 1fr)")
 */
export function getGridTemplate(columns: ColumnSize[]): string {
  return columns
    .map((col, i) => {
      if (i === columns.length - 1) {
        // Check both paths: columnDef (for Column objects) and column.columnDef (for Header objects)
        const minSize = col.columnDef?.minSize ?? col.column?.columnDef?.minSize
        return minSize ? `minmax(${minSize}px, 1fr)` : '1fr'
      }
      return `${col.getSize()}px`
    })
    .join(' ')
}

/**
 * Pure utility to calculate CSS grid-template-columns value with auto-sizing.
 * Columns size automatically based on content, last column fills remaining space.
 *
 * @param columnCount - Number of columns
 * @param lastColumnMin - Optional minimum width for last column (e.g., '250px')
 * @returns CSS grid-template-columns string (e.g., "auto auto auto minmax(250px, 1fr)")
 */
export function getAutoGridTemplate(
  columnCount: number,
  lastColumnMin?: string,
): string {
  const lastCol = lastColumnMin ? `minmax(${lastColumnMin}, 1fr)` : '1fr'
  return Array(columnCount)
    .fill(null)
    .map((_, i) => (i === columnCount - 1 ? lastCol : 'auto'))
    .join(' ')
}
